import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import api from '../utils/axiosInstance';
import { FaCheckCircle } from 'react-icons/fa';
import Recaptcha from '../components/Recaptcha';

export default function Login() {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [nextPath, setNextPath] = useState('/dashboard');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const modal = useModal();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const next = params.get('next');
    const invite = params.get('invite');
    // Only allow relative paths starting with / to prevent open redirect
    if (next && next.startsWith('/') && !next.startsWith('//')) {
      setNextPath(next);
    } else if (invite) {
      setNextPath(`/invite/${encodeURIComponent(invite)}`);
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const { email, password } = formData;
    if (!email || !password) return modal.alert('Both fields are required.', 'warning');

    setLoading(true);
    try {
      const res = await api.post('/api/auth/login', { ...formData, recaptchaToken });
      const data = res.data;
      if (data.success) {
        login(data.role);
        navigate(nextPath || '/dashboard');
      } else {
        modal.alert(data.message || 'Login failed', 'error');
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        modal.alert(err.response.data.message, 'error');
      } else {
        modal.alert('Server error. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const signupPath = nextPath && nextPath !== '/dashboard'
    ? `/signup?next=${encodeURIComponent(nextPath)}`
    : '/signup';

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div className="auth-page flex min-h-screen items-center justify-center p-4">
      <div className="auth-shell flex w-full max-w-[800px] flex-col overflow-hidden bg-surface-card md:flex-row">

        {/* LEFT: Login Form */}
        <div className="w-full md:flex-1 p-8 flex flex-col justify-center">
          <div className="flex items-center mb-8">
            <img
              src="/walytic-logo-blue.png"
              alt="Walytic"
              className="h-8 object-contain dark:hidden"
            />
            <img
              src="/walytic-logo-white.png"
              alt="Walytic"
              className="h-8 object-contain hidden dark:block"
            />
          </div>

          <h2 className="text-2xl font-bold text-ink-primary mb-1">Welcome back</h2>
          <p className="text-ink-muted text-sm mb-6">Sign in to your account to continue</p>

          <div className="input-group mb-4">
            <label className="input-label">Email</label>
            <input
              type="email"
              name="email"
              className="input"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div className="input-group mb-2">
            <label className="input-label">Password</label>
            <input
              type="password"
              name="password"
              className="input"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div className="flex justify-end mb-6">
            <button
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-brand hover:underline"
            >
              Forgot password?
            </button>
          </div>

          <div className="mb-4">
            <Recaptcha onVerify={setRecaptchaToken} onExpire={() => setRecaptchaToken('')} theme="light" />
          </div>

          <button
            className="btn-primary w-full mb-4"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>

          <p className="text-center text-sm text-ink-muted">
            Don't have an account?{' '}
            <button onClick={() => navigate(signupPath)} className="text-brand font-semibold hover:underline">
              Sign up
            </button>
          </p>
        </div>

        {/* RIGHT: Welcome Section */}
        <div className="enterprise-auth-panel hidden flex-1 flex-col justify-center p-8 text-white md:flex">
          <p className="mono-label !text-white/45">Walytic operations</p>
          <h3 className="mt-4 text-3xl font-bold tracking-[-0.03em]">
            Welcome <span className="text-[#7fd0ff]">back.</span>
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-white/70">
            Manage your WhatsApp automation, monitor conversations, and grow your business.
          </p>
          <div className="mt-8 border-t border-white/10">
            {[
              "Automate WhatsApp messaging",
              "Real-time conversation inbox",
              "Analytics and insights dashboard",
            ].map((item, i) => (
              <div key={item} className="flex items-center gap-3 border-b border-white/10 py-3.5">
                <span className="mono-label !text-white/35">{String(i + 1).padStart(2, "0")}</span>
                <FaCheckCircle size={12} className="text-[#0171DE]" />
                <span className="text-sm text-white/80">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
